// Single source of truth for per-tenant (subdomain) configuration.
// Previously this data was duplicated across three files (api.js, Header.js,
// Login.js), so onboarding a new client meant editing all three. Now it lives
// here only. The backend `site-config` (MongoDB) still overrides the logo at
// runtime when it returns one (see Header.js).

import mayanot_logo from "../assets/images/mayanot_logo.jpg";
import yamim_logo from "../assets/images/yamim_logo.png";
import shefaram_logo from "../assets/images/shefaram_logo.png";
import miahcom_logo from "../assets/images/miahcom_logo.png";
import migdal_logo from "../assets/images/migdal_logo.png";
import elad_logo from "../assets/images/elad_logo.png";
import katsrin_logo from "../assets/images/katsrin_logo.png";
import ata_logo from "../assets/images/ata_logo.png";
import yuvallim_logo from "../assets/images/yuvallim_logo.png";
import ans_logo from "../assets/images/ans-logo.png";

const DEFAULTS = {
  apiUrl: null, // when null, computed from the current hostname
  // Default to OUR (ANS) logo when a tenant has no bundled logo AND site-config
  // returns no `site_logo` — so an unconfigured client shows the ANS logo instead
  // of a blank space (mirrors the admin footer fallback in content.php).
  logo: ans_logo,
  logoHeight: "90px",
  logoMarginTop: "30px",
  loginFormMarginTop: "0px",
  // Absolute host used to resolve relative logo URLs coming from site-config.
  logoHost: null,
  // Accessibility statement (הצהרת נגישות) — per-tenant. Each authority has its own
  // accessibility coordinator (רכז נגישות) by law. Fill these per client; when left
  // null the statement shows a graceful fallback instead of fake data.
  //   bodyName: the authority/service name shown in the statement (e.g. "מעיינות השרון").
  //   coordinatorName / coordinatorPhone / coordinatorEmail: the רכז נגישות contact.
  a11y: {
    bodyName: null,
    coordinatorName: null,
    coordinatorPhone: null,
    coordinatorEmail: null,
    // optional: known accessibility exemptions (פטורים מהנגשה), array of strings.
    // Shown as a list in the statement only when non-empty.
    exemptions: [],
  },
};

export const TENANTS = {
  "mayanot-hasharon.anstc.com": {
    apiUrl: "https://mayanot-hasharon-admin.anstc.com/v1/",
    logo: mayanot_logo, logoHeight: "50px", logoMarginTop: "0px",
    loginFormMarginTop: "0px",
    logoHost: "https://mayanot-hasharon-admin.anstc.com",
  },
  "yamim.anstc.com": {
    apiUrl: "https://yamim.anstc.com/backend/v1/",
    logo: yamim_logo, logoHeight: "360px", logoMarginTop: "35px",
    loginFormMarginTop: "90px",
  },
  "shefaram.anstc.com": {
    apiUrl: "https://shefaram.anstc.com/backend/v1/",
    logo: shefaram_logo, logoHeight: "90px", logoMarginTop: "35px",
    loginFormMarginTop: "90px",
  },
  "miahcom.anstc.com": {
    apiUrl: "https://miahcom.anstc.com/backend/v1/",
    logo: miahcom_logo, logoHeight: "90px", logoMarginTop: "35px",
    loginFormMarginTop: "90px",
  },
  "migdal-haemeq.anstc.com": {
    apiUrl: "https://migdal-haemeq.anstc.com/backend/v1/",
    logo: migdal_logo, logoHeight: "90px", logoMarginTop: "35px",
    loginFormMarginTop: "90px",
  },
  "elad.anstc.com": {
    apiUrl: "https://elad.anstc.com/backend/v1/",
    logo: elad_logo, logoHeight: "90px", logoMarginTop: "30px",
    loginFormMarginTop: "60px",
  },
  "katsrin.anstc.com": {
    apiUrl: "https://katsrin.anstc.com/backend/v1/",
    logo: katsrin_logo, logoHeight: "90px", logoMarginTop: "30px",
    loginFormMarginTop: "0px",
  },
  "mayanotata.anstc.com": {
    apiUrl: "https://mayanotata.anstc.com/backend/v1/",
    logo: ata_logo, logoHeight: "90px", logoMarginTop: "30px",
    loginFormMarginTop: "80px",
  },
  "yuvallim.anstc.com": {
    apiUrl: "https://yuvallim.anstc.com/backend/v1/",
    logo: yuvallim_logo, logoHeight: "90px", logoMarginTop: "30px",
    loginFormMarginTop: "80px",
  },
  "ans.mipo.co.il": {
    apiUrl: "https://appointments-ans.mipo.co.il/v1/",
    logoHost: "https://appointments-ans.mipo.co.il",
  },
};

// LOCAL DEV ONLY: localhost / 127.0.0.1 / any private LAN IP points at the LOCAL
// backend on the SAME host:3001 — so it works both on the dev machine AND from a
// phone browsing the dev machine's IP on the same Wi-Fi. Keeps mayanot logo/sizing.
const __localHost = getHostname();
/* ★★★ הזיהוי מתבסס גם על **הפורט** ולא רק על טווחי-IP פרטיים.
   ⚠️ הבאג שנמצא 05/08: הרשת חילקה למחשב את הכתובת `9.38.15.4`, שאינה
      באף אחד מהטווחים הפרטיים שנבדקו כאן. התוצאה בטלפון הייתה
      `apiUrl = https://9.38.15.4/backend/v1/` — כלומר **HTTPS על 443**
      לשרת שאינו מקשיב שם. כל קריאה נתלתה עד timeout, ומכאן שני תסמינים
      שנראו כשני באגים נפרדים:
        · טעינה של מעל שתי דקות בטלפון (ורק בו);
        · `site-config` חזר ריק ⇒ הקישור "קבלו את הקוד במייל" לא הופיע.
   ★ שרת-הפיתוח של CRA מאזין ב-3000 ותמיד ב-HTTP. `protocol === 'http:'`
     + הפורט הוא סימן חד-משמעי לפיתוח, ואינו תלוי בטווח שהראוטר מחלק.
   ⚠️ נשמר גם תנאי-הטווחים: הוא מכסה מקרה שבו מגישים את ה-build מפורט אחר. */
const __devPort = typeof window !== "undefined" && window.location
  ? window.location.port === "3000" && window.location.protocol === "http:"
  : false;
if (
  __localHost === "localhost" ||
  __localHost === "127.0.0.1" ||
  __devPort ||
  /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(__localHost)
) {
  /* ★ בפיתוח ברירת-המחדל היא **לוגו ANS** (מ-DEFAULTS), לא מעיינות (בקשת-משתמש
     11/08). כך כשה-site-config של הבקנד אינו זמין (למשל מהטלפון שאינו מגיע
     ל-:3001) נראה לוגו-ANS ולא לוגו-לקוח. אין spread ל-config של מעיינות. */
  TENANTS[__localHost] = {
    apiUrl: `http://${__localHost}:3001/v1/`,
    logoHost: `http://${__localHost}:3001`,
  };
}

export function getHostname() {
  let hostname = window.location.hostname;
  if (hostname.startsWith("www.")) hostname = hostname.substring(4);
  return hostname;
}

/** Resolved config for the current hostname, with defaults filled in. */
export function getTenant() {
  return { ...DEFAULTS, ...(TENANTS[getHostname()] || {}) };
}

/** API base URL for the current tenant (falls back to `<host>/backend/v1/`). */
export const API_URL =
  getTenant().apiUrl || `https://${getHostname()}/backend/v1/`;
